import type { ModelInfo, Profile, ProfileScope } from "@fusioncraft/shared";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent } from "react";
import { api } from "../api";

let modelsPromise: ReturnType<typeof api.models> | null = null;
function loadModels() {
  modelsPromise ??= api.models().catch((e) => {
    modelsPromise = null;
    throw e;
  });
  return modelsPromise;
}

function price(m: ModelInfo): string {
  const f = (n: number) => (n < 0.1 ? n.toFixed(3) : n.toFixed(2)).replace(/\.?0+$/, "") || "0";
  return `$${f(m.promptPrice)} · $${f(m.completionPrice)}`;
}

/** Every row is exactly this tall (see .model-row), which is what makes the list cheap to virtualise. */
const ROW_HEIGHT = 48;
const OVERSCAN = 6;

// Hand-picked hues for the vendors that make up most of the list, spread around the wheel.
// Saturation 0 renders grey (xAI's brand is black and white).
const VENDOR_COLORS: Record<string, [hue: number, saturation: number]> = {
  openai: [160, 65],
  google: [215, 75],
  qwen: [265, 55],
  anthropic: [18, 70],
  mistralai: [42, 85],
  "z-ai": [125, 50],
  deepseek: [190, 70],
  nvidia: [90, 60],
  minimax: [330, 65],
  "x-ai": [0, 0],
  moonshotai: [295, 50],
  meta: [240, 55],
  "meta-llama": [240, 55],
};

function vendorOf(id: string): string {
  return id.replace(/^~/, "").split("/")[0] ?? id;
}

/** Every other vendor gets a stable hue derived from its name, so it matches wherever it appears. */
function vendorColor(vendor: string): [number, number] {
  const known = VENDOR_COLORS[vendor];
  if (known) return known;
  let hash = 2166136261;
  for (let i = 0; i < vendor.length; i++) hash = Math.imul(hash ^ vendor.charCodeAt(i), 16777619);
  return [Math.round(((hash >>> 0) * 137.508) % 360), 55];
}

const releaseDate = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", year: "numeric" });

function ModelPicker({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const [models, setModels] = useState<ModelInfo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState(250);
  const scrolledToSelection = useRef(false);

  useEffect(() => {
    loadModels()
      .then((r) => {
        setModels(r.models);
        if (!value) onChange(r.defaultModel);
      })
      .catch((e) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!models) return [];
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    const list = terms.length
      ? models.filter((m) => terms.every((t) => m.id.toLowerCase().includes(t) || m.name.toLowerCase().includes(t)))
      : models;
    return list;
  }, [models, query]);

  const scrollTo = (top: number) => {
    if (listRef.current) listRef.current.scrollTop = top;
    setScrollTop(top);
  };

  // Open with the selected model centred in view, once the list has loaded.
  useLayoutEffect(() => {
    if (scrolledToSelection.current || !models || !value || !listRef.current) return;
    scrolledToSelection.current = true;
    const index = models.findIndex((m) => m.id === value);
    const height = listRef.current.clientHeight;
    setViewport(height);
    if (index > 0) scrollTo(Math.max(0, index * ROW_HEIGHT - (height - ROW_HEIGHT) / 2));
  }, [models, value]);

  const first = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const last = Math.min(filtered.length, Math.ceil((scrollTop + viewport) / ROW_HEIGHT) + OVERSCAN);

  if (error) return <p className="form-error">{error}</p>;

  return (
    <div className="model-picker">
      <input
        className="input"
        type="search"
        placeholder={models ? `Search ${models.length} models` : "Loading models…"}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          scrollTo(0);
        }}
        aria-label="Search models"
      />
      <div
        className="model-list"
        role="listbox"
        aria-label="Models"
        ref={listRef}
        onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
      >
        <div className="model-list-inner" style={{ height: filtered.length * ROW_HEIGHT }}>
        {filtered.slice(first, last).map((m, i) => {
          const vendor = vendorOf(m.id);
          const [hue, sat] = vendorColor(vendor);
          return (
            <button
              type="button"
              role="option"
              aria-selected={m.id === value}
              key={m.id}
              className="model-row"
              style={{ "--vh": hue, "--vs": `${sat}%`, top: (first + i) * ROW_HEIGHT } as CSSProperties}
              onClick={() => onChange(m.id)}
            >
              <span className="model-name">{m.name}</span>
              <span className="model-id">
                <span className="model-vendor">{vendor}</span>
                {m.id.slice(m.id.indexOf(vendor) + vendor.length)}
              </span>
              <span className="model-price">{price(m)}</span>
              {m.created > 0 && <span className="model-date">{releaseDate.format(m.created * 1000)}</span>}
            </button>
          );
        })}
        </div>
        {models && filtered.length === 0 && <p className="model-empty">No model matches “{query}”.</p>}
      </div>
      <p className="field-hint">Prices are USD per million input · output tokens, billed through OpenRouter.</p>
    </div>
  );
}

export function ProfileForm({
  onCreated,
  onCancel,
  suggestedName = "",
}: {
  onCreated: (p: Profile) => void;
  onCancel?: () => void;
  suggestedName?: string;
}) {
  const [name, setName] = useState(suggestedName);
  const [model, setModel] = useState("");
  const [scope, setScope] = useState<ProfileScope>("shared");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!model) return setError("Pick a model.");
    setBusy(true);
    setError(null);
    try {
      const { profile } = await api.createProfile(name, model, scope);
      onCreated(profile);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <form className="profile-form" onSubmit={submit}>
      <label className="field">
        <span>Profile name</span>
        <input
          className="input"
          value={name}
          maxLength={40}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Slow and literal"
          required
          autoFocus
        />
      </label>

      <div className="field">
        <span>Model</span>
        <ModelPicker value={model} onChange={setModel} />
      </div>

      <fieldset className="field">
        <legend>World</legend>
        <div className="scope-options">
          <label className="scope-option">
            <input type="radio" name="scope" checked={scope === "shared"} onChange={() => setScope("shared")} />
            <span>
              <strong>Shared</strong>
              Fusions are pooled with everyone on this model. Cheaper and faster, and first discoveries count globally.
            </span>
          </label>
          <label className="scope-option">
            <input type="radio" name="scope" checked={scope === "private"} onChange={() => setScope("private")} />
            <span>
              <strong>Private</strong>
              A world of your own. Nothing is shared, so the same pair can lead somewhere entirely different.
            </span>
          </label>
        </div>
      </fieldset>

      <p className="field-hint">The model and world can't be changed later, since every fusion depends on them.</p>
      {error && <p className="form-error" role="alert">{error}</p>}

      <div className="form-actions">
        {onCancel && (
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button className="btn btn-primary" disabled={busy || !model}>
          {busy ? "Creating…" : "Create profile"}
        </button>
      </div>
    </form>
  );
}
